import React, { useCallback, useMemo } from "react";
import { Helmet } from "react-helmet";

import { getCollectionCortisData } from "./CollectionCortisData";
import { COLLECTION_ITEM_TYPES } from "./CollectionCortisConstants";
import { IMAGE_CORTIS_MEMBERS } from "../../library/constants/cortis";
import CollectionImageCaptionItem from "./components/CollectionImageCaptionItem.jsx";
import CollectionIntroContentItem from "./components/CollectionIntroContentItem.jsx";
import CollectionQnaItem from "./components/CollectionQnaItem.jsx";
import CollectionQuoteItem from "./components/CollectionQuoteItem.jsx";
import CollectionSectionTitleItem from "./components/CollectionSectionTitleItem.jsx";
import CollectionSubTitleItem from "./components/CollectionSubTitleItem.jsx";
import CollectionTitleItem from "./components/CollectionTitleItem.jsx";
import CollectionWishlist from "./components/CollectionWishlist.jsx";
import CollectionSpotifyEmbed from "./components/CollectionSpotifyEmbed.jsx";

import "./CollectionCortis.css";

export default function CollectionCortis() {
  const items = useMemo(() => getCollectionCortisData(), []);

  const renderItem = useCallback((item) => {
    switch (item.type) {
      case COLLECTION_ITEM_TYPES.COLLECTION_TITLE:
        return (
            <CollectionTitleItem
                name={item.value.name}
                subName={item.value.subName}
            />
        );
      case COLLECTION_ITEM_TYPES.COLLECTION_SUBTITLE:
        return <CollectionSubTitleItem value={item.value} />;
      case COLLECTION_ITEM_TYPES.COLLECTION_IMAGE_CAPTION:
        return (
            <CollectionImageCaptionItem
                url={item.value.url}
                caption={item.value.caption}
                aspectRatio={item.value.aspectRatio}
            />
        );
      case COLLECTION_ITEM_TYPES.COLLECTION_QNA:
        return (
            <CollectionQnaItem
                question={item.value.question}
                answer={item.value.answer}
                answerPrefix={item.value.answerPrefix}
            />
        );
      case COLLECTION_ITEM_TYPES.COLLECTION_SECTION_TITLE:
        return (
            <CollectionSectionTitleItem
                title={
                  item.value && item.value.title ? item.value.title : undefined
                }
            />
        );
      case COLLECTION_ITEM_TYPES.COLLECTION_QUOTE:
        return (
            <CollectionQuoteItem
                text={item.value && item.value.text ? item.value.text : ""}
                author={item.value && item.value.author ? item.value.author : ""}
            />
        );
      case COLLECTION_ITEM_TYPES.COLLECTION_WISHLIST:
        return <CollectionWishlist name={item.value.name} />;
      case COLLECTION_ITEM_TYPES.COLLECTION_INTRO_CONTENT:
        return <CollectionIntroContentItem />;
      case COLLECTION_ITEM_TYPES.COLLECTION_SPOTIFY_EMBED:
        return <CollectionSpotifyEmbed />;
      default:
        return null;
    }
  }, []);

  const pageTitle = "CORTIS | 후루츠패밀리";
  const pageDescription = "있는 그대로. 꾸미지 않은 청춘. CORTIS 다섯 패션 다섯 취향";
  const canonicalUrl = "https://fruitsfamily.com/feature/cortis";
  const ogImage = IMAGE_CORTIS_MEMBERS;

  return (
      <section className="CollectionCortis">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          <link rel="canonical" href={canonicalUrl} />

          {/* Open Graph */}
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:site_name" content="후루츠패밀리" />
          <meta property="og:image" content={ogImage} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={pageTitle} />
          <meta name="twitter:description" content={pageDescription} />
          <meta name="twitter:image" content={ogImage} />
        </Helmet>
        <div className="CollectionCortis-container">
          <div className="CollectionCortis-list">
          {items.map((item) => (
              <div key={item.id} className="CollectionCortis-listItem">
                {renderItem(item)}
              </div>
          ))}
        </div>
        </div>
      </section>
  );
}